import PocketBase from 'pocketbase'
import type { Comment, JourneyState, OnboardingInput, Plan, SubscriptionSummary, UserProfile } from '../types'

export const pocketbaseUrl = import.meta.env.VITE_POCKETBASE_URL || 'http://127.0.0.1:8090'
export const pb = new PocketBase(pocketbaseUrl)
pb.autoCancellation(false)

export async function isPocketBaseAvailable() {
  try {
    await pb.health.check()
    return true
  } catch {
    return false
  }
}

export function currentProfile(): UserProfile | null {
  const record = pb.authStore.record
  if (!record) return null
  return {
    id: record.id,
    name: record.name || record.email.split('@')[0],
    email: record.email,
    intention: record.intention,
    experience: record.experience,
    preferredTime: record.preferred_time,
    notifications: record.notifications,
    onboardingComplete: record.onboarding_complete,
  }
}

export async function signIn(email: string, password: string) {
  await pb.collection('users').authWithPassword(email, password)
  return currentProfile()
}

export async function signUp(name: string, email: string, password: string) {
  await pb.collection('users').create({ email, password, passwordConfirm: password, name })
  return signIn(email, password)
}

export async function saveOnboarding(data: OnboardingInput) {
  const response = await pb.send<{ user: UserProfile }>('/api/mcv/onboarding', {
    method: 'POST', body: data,
  })
  await pb.collection('users').authRefresh()
  return response.user
}

export async function getJourney() {
  return pb.send<JourneyState>('/api/mcv/state', { method: 'GET' })
}

export async function mutateProgress(activityId: string, action: string, data: Record<string, unknown> = {}) {
  return pb.send<JourneyState>('/api/mcv/progress', {
    method: 'POST', body: { activityId, action, ...data },
  })
}

export async function saveReflection(reflection: string) {
  return pb.send<JourneyState>('/api/mcv/reflection', {
    method: 'POST', body: { reflection },
  })
}

export async function getComments(activityId: string) {
  return pb.send<Comment[]>(`/api/mcv/comments/${activityId}`, { method: 'GET' })
}

export async function postComment(activityId: string, text: string, parentId?: string) {
  return pb.send<Comment[]>('/api/mcv/comments', {
    method: 'POST', body: { activityId, text, parentId: parentId || '' },
  })
}

export async function toggleCommentLike(commentId: string) {
  return pb.send<Comment[]>(`/api/mcv/comments/${commentId}/like`, { method: 'POST' })
}

export async function listOfferedPlans() {
  return pb.collection('plans').getFullList<Plan>({ filter: 'is_offered = true', sort: 'name' })
}

export async function myActiveSubscription() {
  try {
    return await pb.collection('subscriptions').getFirstListItem<SubscriptionSummary>('status = "active"', { expand: 'plan' })
  } catch {
    return null
  }
}

export async function startCheckout(planId: string) {
  const origin = window.location.origin
  return pb.send<{ url: string }>('/api/mcv/checkout', {
    method: 'POST',
    body: { planId, successUrl: `${origin}/assinar?status=success`, cancelUrl: `${origin}/assinar?status=cancel` },
  })
}

export async function openBillingPortal() {
  const origin = window.location.origin
  return pb.send<{ url: string }>('/api/mcv/billing-portal', {
    method: 'POST',
    body: { returnUrl: `${origin}/perfil` },
  })
}
